// components/MarketList.jsx - 市场列表组件
import React, { useState, useEffect, useCallback, useRef } from 'react';
import './MarketList.css';
import MarketCard from './MarketCard';

import { formatUnits, ethers } from 'ethers';
import { AroundUIHelperContract } from '../contracts/AroundUIHelper';
import { AroundPoolFactoryContract } from '../contracts/AroundPoolFactory';
import { AroundMarketContract } from '../contracts/AroundMarket';
import { useEthers } from '../context/EthersContext';
import { getRpcUrl } from '../utils/address';
import { useNavigate } from 'react-router-dom';

const PAGE_SIZE = 10;
const rpcUrl = getRpcUrl();

// Provider 将在组件内通过 useEthers 或 compat 获取

// Safe contract initialization helper
const createContractInstance = (address, ContractClass, provider) => {
  if (!address) {
    console.error("Missing Contract Address. Check REACT_APP_AROUND_UIHELPER_ADDRESS and REACT_APP_AROUND_POOL_FACTORY_ADDRESS");
    return null;
  }
  if (!provider) {
    console.error("Missing Provider. No wallet connected and no RPC URL configured.");
    return null;
  }
  try {
    return new ContractClass(address, provider);
  } catch (error) {
    console.error("Failed to initialize contract:", error);
    return null;
  }
};

// 合约实例改为在组件中基于 provider 初始化

// Debug logs moved into component after provider is resolved

const getCategoryName = (typeId) => {
  const type = Number(typeId);
  switch (type) {
    case 0: return 'other';
    case 1: return 'crypto';
    case 2: return 'sports';
    case 3: return 'gaming';
    case 4: return 'politics';
    case 5: return 'economy';
    case 6: return 'technology';
    case 7: return 'weather';
    default: return 'other';
  }
};

// Resolve UI category value to on-chain type id
const resolveTypeId = (cat) => {
  if (typeof cat === 'number') return cat;
  if (typeof cat === 'string') {
    const lower = cat.toLowerCase();
    switch (lower) {
      case 'other': return 0;
      case 'crypto': return 1;
      case 'sports': return 2;
      case 'gaming': return 3;
      case 'politics': return 4;
      case 'economy': return 5;
      case 'technology': return 6;
      case 'weather': return 7;
      default: {
        const n = Number(cat);
        return Number.isFinite(n) ? n : 0;
      }
    }
  }
  const n = Number(cat);
  return Number.isFinite(n) ? n : 0;
};

const MarketList = ({ category, onBetClick }) => {
  const { provider: ctxProvider, chainId: ctxChainId, addresses: ctxAddrs } = useEthers();
  const provider = ctxProvider || (rpcUrl ? new ethers.JsonRpcProvider(rpcUrl) : null);
  const uiHelperAddress = ctxAddrs?.AroundUIHelper || '';
  const marketAddress = ctxAddrs?.AroundPoolFactory || '';
  const chainId = ctxChainId;
  const uiHelper = React.useMemo(() => createContractInstance(uiHelperAddress, AroundUIHelperContract, provider), [provider, uiHelperAddress]);
  const marketContract = React.useMemo(() => createContractInstance(marketAddress, AroundPoolFactoryContract, provider), [provider, marketAddress]);
  React.useEffect(() => {
    console.log("Environment Config:", {
      chainId,
      uiHelperAddress: uiHelperAddress || "MISSING",
      marketAddress: marketAddress || "MISSING",
      hasProvider: !!provider,
      rpcUrl: rpcUrl || "DEFAULT/MISSING"
    });
  }, [provider, uiHelperAddress, marketAddress, chainId]);
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const loadKeyRef = useRef(0);
  const navigate = useNavigate();

  // 滚动加载更多数据的函数
  const loadMoreMarkets = useCallback(() => {
    if (!loading && hasMore) {
      setPage(prev => prev + 1);
    }
  }, [loading, hasMore]);

  // 滚动事件处理
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight;
    const clientHeight = document.documentElement.clientHeight;

    // 当滚动到底部100px范围内时加载更多
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      loadMoreMarkets();
    }
  }, [loadMoreMarkets]);

  // 添加滚动事件监听
  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const formatEthValue = (value) => {
    try {
      if (!value) return 0;
      const formatted = formatUnits(value, 18);
      // Keep max 2 decimal places
      return parseFloat(Number(formatted).toFixed(2));
    } catch (e) {
      console.error("Error formatting value:", value, e);
      return 0;
    }
  };

  // 加载市场数据
  const loadMarkets = useCallback(async () => {
    if (loading) return;
    // Check if contracts are initialized
    if (!uiHelper || !marketContract) {
      console.warn("Contracts not initialized. Check environment variables and wallet connection.");
      setLoading(false);
      return;
    }

    const myKey = (loadKeyRef.current + 1);
    loadKeyRef.current = myKey;
    setLoading(true);
    try {
      // Verify factory address has code on-chain to avoid BAD_DATA 0x decode on production
      try {
        const code = await provider.getCode(marketAddress);
        if (!code || code === '0x') {
          console.warn('Factory contract code missing at', marketAddress);
          setHasMore(false);
          setLoading(false);
          return;
        }
      } catch (e) { console.error('code check failed', e); }

      // Verify provider network matches configured chainId
      try {
        const net = await provider.getNetwork();
        const cfgChain = chainId;
        if (Number(net?.chainId) !== Number(cfgChain)) {
          console.warn('Network mismatch', { providerChainId: Number(net?.chainId), configuredChainId: Number(cfgChain) });
          setHasMore(false);
          setLoading(false);
          return;
        }
      } catch (e) { console.error('network check failed', e); }

      const safePage = Number(page) || 1;
      const safePageSize = Number(PAGE_SIZE) || 10;

      let totalLength = 0;
      let lastId = -1;

      if (category && category !== 'all') {
        const marketType = resolveTypeId(category);
        try {
          totalLength = Number(await marketContract.getMarketGroupLength(marketType));
        } catch (e) {
          console.warn('getMarketGroupLength failed, fallback to ALL', e?.message);
          const nextId = await marketContract.marketId();
          totalLength = Math.max(0, Number(nextId) - 1);
        }
      } else {
        try {
          const nextId = await marketContract.marketId();
          lastId = Number(nextId) - 1;
          totalLength = Math.max(0, lastId + 1);
        } catch (e) {
          console.warn('marketId failed, treat as empty list', e?.message);
          lastId = -1;
          totalLength = 0;
        }
      }

      console.log('Total Length:', totalLength);

      const totalPages = Math.ceil((Number(totalLength) || 0) / safePageSize);

      // Standard reverse pagination
      const targetIndex = totalPages - safePage;
      const fetchIndices = targetIndex >= 0 ? [targetIndex] : [];
      console.log('[ReversePagination]', { totalLength, totalPages, uiPage: safePage, targetIndex, pageSize: safePageSize, category });

      if (fetchIndices.length === 0) {
        setHasMore(false);
        setLoading(false);
        return;
      }

      console.log('[分页调用] Parameters:', { fetchIndices, safePageSize, category });

      let combinedMarkets = [];

      // Prepare AroundMarket reader for fallback
      const aroundMarketAddress = ctxAddrs?.AroundMarket || '';
      const marketReader = aroundMarketAddress && provider ? new AroundMarketContract(aroundMarketAddress, provider) : null;

      for (const index of fetchIndices) {
        let batchResult;
        let idsLocalResolved = undefined;
        if (category && category !== 'all') {
          const marketType = resolveTypeId(category);
          // Descending group indices for reverse pagination within category
          const endGroupIndex = totalLength - (safePage - 1) * safePageSize - 1; // zero-based
          const startGroupIndex = Math.max(0, endGroupIndex - safePageSize + 1);
          const groupIndices = [];
          for (let gi = endGroupIndex; gi >= startGroupIndex; gi--) groupIndices.push(gi);
          const idGroupLocalRaw = await Promise.all(groupIndices.map((gi) => marketContract.indexMarketTypeData(marketType, gi)));
          const idGroupLocal = idGroupLocalRaw.map(id => id?.toString?.() || '');
          const mdPromises = idGroupLocal.map(id => uiHelper.indexMarketData(id).catch(() => null));
          const mdResults = await Promise.all(mdPromises);
          const marketInfoGroup = mdResults.map(r => r?.thisMarketInfo || {});
          const liqudityInfoGroup = mdResults.map(r => r?.thisLiqudityInfo || {});
          const poolInfoGroup = mdResults.map(r => r?.thisPoolInfo || {});
          const oracleInfoGroup = mdResults.map(r => r?.thisOracleInfo || {});
          const yesPriceGroup = await Promise.all(idGroupLocal.map(id => uiHelper.getYesPrice(id).catch(() => 0)));
          const noPriceGroup = await Promise.all(idGroupLocal.map(id => uiHelper.getNoPrice(id).catch(() => 0)));
          const estPromises = idGroupLocal.map(async (id) => {
            try {
              const amt = ethers.parseUnits('100', 6);
              const wy = await uiHelper.estimateWinAmount(1, amt, id).catch(() => 0n);
              console.log('%c🚀[-------wy]-241:', 'color: #372ee4', wy);
              const wn = await uiHelper.estimateWinAmount(2, amt, id).catch(() => 0n);
              console.log('%c🚀[wn]-243:', 'color: #3584d6', wn);
              const best = wy > wn ? wy : wn;
              return best;
            } catch { return 0n; }
          });
          const estimateWinAmountGroup = await Promise.all(estPromises);
          batchResult = { marketInfoGroup, liqudityInfoGroup, poolInfoGroup, oracleInfoGroup, yesPriceGroup, noPriceGroup, estimateWinAmountGroup };
          console.log('%c🚀[batchResult]-234:', 'color: #389a9f', batchResult);
          idsLocalResolved = idGroupLocal;
        } else {
          const endId = (lastId >= 0 ? lastId : (totalLength - 1)) - (safePage - 1) * safePageSize;
          const startId = Math.max(0, endId - safePageSize + 1);
          const idsLocal = [];
          for (let id = endId; id >= startId; id--) idsLocal.push(String(id));
          console.log('[ALL] Fetch id range', { startId, endId, count: idsLocal.length });

          const mdPromises = idsLocal.map(id => uiHelper.indexMarketData(ethers.toBigInt(id)).catch(() => null));
          const mdResults = await Promise.all(mdPromises);
          const marketInfoGroup = [];
          const liqudityInfoGroup = [];
          const poolInfoGroup = mdResults.map(r => r?.thisPoolInfo || {});
          const oracleInfoGroup = mdResults.map(r => r?.thisOracleInfo || {});
          for (let i = 0; i < idsLocal.length; i++) {
            const r = mdResults[i];
            if (r && r.thisMarketInfo) {
              marketInfoGroup.push(r.thisMarketInfo || {});
              liqudityInfoGroup.push(r.thisLiqudityInfo || {});
            } else if (marketReader) {
              try {
                const idBn = ethers.toBigInt(idsLocal[i]);
                const md = await marketReader.getMarketDetails(idBn);
                marketInfoGroup.push(md || {});
                liqudityInfoGroup.push({});
              } catch {
                marketInfoGroup.push({});
                liqudityInfoGroup.push({});
              }
            } else {
              marketInfoGroup.push({});
              liqudityInfoGroup.push({});
            }
          }
          const yesPriceGroup = await Promise.all(idsLocal.map(id => uiHelper.getYesPrice(ethers.toBigInt(id)).catch(() => 0)));
          const noPriceGroup = await Promise.all(idsLocal.map(id => uiHelper.getNoPrice(ethers.toBigInt(id)).catch(() => 0)));
          const estPromises = idsLocal.map(async (id) => {
            try {
              const amt = ethers.parseUnits('100', 6);
              const wy = await uiHelper.estimateWinAmount(1, amt, ethers.toBigInt(id)).catch(() => 0n);
              console.log('%c🚀[wy]-292:', 'color: #508902', wy);
              const wn = await uiHelper.estimateWinAmount(2, amt, ethers.toBigInt(id)).catch(() => 0n);
              const best = wy > wn ? wy : wn;
              return best;
            } catch { return 0n; }
          });
          const estimateWinAmountGroup = await Promise.all(estPromises);
          batchResult = { marketInfoGroup, liqudityInfoGroup, poolInfoGroup, oracleInfoGroup, yesPriceGroup, noPriceGroup, estimateWinAmountGroup };
          console.log('%c🚀[batchResult]-272:', 'color: #cb3ebf', batchResult);
          idsLocalResolved = idsLocal;
        }

        const { marketInfoGroup, liqudityInfoGroup, poolInfoGroup, oracleInfoGroup, yesPriceGroup, noPriceGroup } = batchResult;
        const estWinGroup = batchResult.estimateWinAmountGroup || [];
        const buyOutGroup = batchResult.getBuyOutputGroup || batchResult.buyOutputGroup || [];
        const sellOutGroup = batchResult.getSellOutputGroup || batchResult.sellOutputGroup || [];

        // For ALL category, fetch marketType via factory to ensure correct classification
        let typesGroup = [];
        if ((!category || category === 'all') && Array.isArray(idsLocalResolved) && idsLocalResolved.length) {
          try {
            const typePromises = idsLocalResolved.map((idStr) => marketContract.getMarketPlace(ethers.toBigInt(idStr)));
            const places = await Promise.all(typePromises);
            typesGroup = places.map(p => Number(p?.marketType ?? p?.[0] ?? 0));
          } catch (e) { console.error('getMarketPlace failed', e); }
        }

        const batchMarkets = Array.isArray(marketInfoGroup) ? marketInfoGroup.map((marketInfo, idx) => {
          const liquidityInfo = Array.isArray(liqudityInfoGroup) ? liqudityInfoGroup[idx] : {};
          const yesPrice = Array.isArray(yesPriceGroup) ? yesPriceGroup[idx] : 0;
          const noPrice = Array.isArray(noPriceGroup) ? noPriceGroup[idx] : 0;
          const estWinRaw = Array.isArray(estWinGroup) ? estWinGroup[idx] : 0n;
          const buyOutRaw = Array.isArray(buyOutGroup) ? buyOutGroup[idx] : null;
          const sellOutRaw = Array.isArray(sellOutGroup) ? sellOutGroup[idx] : null;

          const idFromInfo = marketInfo?.thisMarketId?.toString?.();
          const calculatedId = (idsLocalResolved && idsLocalResolved[idx]) ? idsLocalResolved[idx] : (idFromInfo || ((index * safePageSize) + idx + 1).toString());
          if (!calculatedId) return null;

          const poolInfo = Array.isArray(poolInfoGroup) ? poolInfoGroup[idx] : {};
          const questFromPool = Array.isArray(poolInfo) ? (poolInfo?.[4] || '') : (poolInfo?.quest || '');
          const oracleItem = Array.isArray(oracleInfoGroup) ? oracleInfoGroup[idx] : undefined;
          const questFromOracle = Array.isArray(oracleItem) ? (oracleItem?.[7] || '') : (oracleItem?.quest || '');
          const poolTitle = typeof questFromPool === 'string' ? questFromPool.trim() : String(questFromPool || '').trim();
          const oracleTitle = typeof questFromOracle === 'string' ? questFromOracle.trim() : String(questFromOracle || '').trim();

          return {
            id: calculatedId.toString(),
            title: (oracleTitle || `Market #${calculatedId}`),
            category: getCategoryName(
              (!category || category === 'all')
                ? (typesGroup[idx] ?? marketInfo?.marketType)
                : resolveTypeId(category)
            ),
            volume: Number(ethers.formatUnits((liquidityInfo?.volume ?? liquidityInfo?.[7] ?? 0), 6)),
            participants: Number(marketInfo?.participants || 0),
            yesPrice: parseFloat(Number(ethers.formatUnits(yesPrice || 0, 18)).toFixed(4)),
            noPrice: parseFloat(Number(ethers.formatUnits(noPrice || 0, 18)).toFixed(4)),
            endDate: marketInfo?.endTime ? new Date(Number(marketInfo.endTime) * 1000) : null,
            liquidity: {
              virtual: Number(ethers.formatUnits((liquidityInfo?.virtualLiquidity ?? liquidityInfo?.[0] ?? 0), 6)),
              real: Number(ethers.formatUnits((liquidityInfo?.tradeCollateralAmount ?? liquidityInfo?.[1] ?? 0), 6)) + Number(ethers.formatUnits((liquidityInfo?.lpCollateralAmount ?? liquidityInfo?.[2] ?? 0), 6))
            },
            resolution: marketInfo?.result === 1 ? 'yes' : marketInfo?.result === 2 ? 'no' : 'pending',
            estWinAmount: Number(ethers.formatUnits(estWinRaw || 0n, 6)),
            buyOutput: {
              output: buyOutRaw && (buyOutRaw.output ?? buyOutRaw[0]) ? parseFloat(Number(ethers.formatUnits((buyOutRaw.output ?? buyOutRaw[0]) || 0, 18)).toFixed(2)) : 0,
              fee: buyOutRaw && (buyOutRaw.fee ?? buyOutRaw[1]) ? Number(ethers.formatUnits((buyOutRaw.fee ?? buyOutRaw[1]) || 0, 6)) : 0
            },
            sellOutput: {
              output: sellOutRaw && (sellOutRaw.output ?? sellOutRaw[0]) ? Number(ethers.formatUnits((sellOutRaw.output ?? sellOutRaw[0]) || 0, 6)) : 0,
              fee: sellOutRaw && (sellOutRaw.fee ?? sellOutRaw[1]) ? Number(ethers.formatUnits((sellOutRaw.fee ?? sellOutRaw[1]) || 0, 6)) : 0
            }
          };
        }).filter(Boolean) : [];

        combinedMarkets = [...combinedMarkets, ...batchMarkets];
      }

      const newMarkets = combinedMarkets;

      if (myKey !== loadKeyRef.current) {
        // A newer request has been started; discard this result
        return;
      }
      if (page === 1) {
        const seen = new Set();
        const uniq = [];
        for (const m of newMarkets) {
          const k = String(m.id);
          if (seen.has(k)) continue;
          seen.add(k);
          uniq.push(m);
        }
        setMarkets(uniq);
      } else {
        setMarkets(prev => {
          const combined = [...prev, ...newMarkets];
          const seen = new Set();
          const uniq = [];
          for (const m of combined) {
            const k = String(m.id);
            if (seen.has(k)) continue;
            seen.add(k);
            uniq.push(m);
          }
          return uniq;
        });
      }

      // Calculate hasMore
      const nextIndexToCheck = totalPages - (safePage + 1);
      if (myKey === loadKeyRef.current) {
        setHasMore(nextIndexToCheck >= 0);
      }
    } catch (err) {
      console.error('Error loading markets:', err);
    } finally {
      if (loadKeyRef.current === myKey) {
        setLoading(false);
      }
    }
  }, [category, page, provider, uiHelper, marketContract, uiHelperAddress, marketAddress, chainId]);

  // 初始加载和分类变更时重置
  useEffect(() => {
    setPage(1);
    setMarkets([]);
    setHasMore(true);
    loadKeyRef.current = 0;
    setLoading(false);
  }, [category, chainId, uiHelperAddress, marketAddress]);

  // 加载更多数据
  useEffect(() => {
    loadMarkets();
  }, [page, loadMarkets]);



  // 处理市场卡片点击
  const handleCardClick = (market) => {
    navigate(`/market/${market.id}`, { state: { market } });
  };

  return (
    <div className="market-list">
      <div className="list-header">
        <h2>Markets</h2>
        <div className="sort-options">
          <select className="sort-select">
            <option>Sort by: Volume</option>
            <option>Sort by: Newest</option>
            <option>Sort by: Ending Soon</option>
            <option>Sort by: Liquidity</option>
          </select>
        </div>
      </div>

      <div className="markets-grid">
        {markets.map(market => (
          <MarketCard
            key={market.id}
            market={market}
            onClick={() => handleCardClick(market)}
            onBetClick={onBetClick}
          />
        ))}
      </div>

      {loading && page === 1 && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading markets...</p>
        </div>
      )}
      {loading && page > 1 && (
        <div className="loading-more">
          <div className="loading-spinner-small"></div>
          <p>Loading more markets...</p>
        </div>
      )}
      {!hasMore && markets.length > 0 && (
        <div className="end-message">
          <p>No more data</p>
        </div>
      )}
    </div>
  );
}

export default MarketList;
